import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import apiService from '../services/api';

/**
 * Creator Analytics Dashboard
 * Comprehensive analytics for content creators
 */

const TIME_RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const METRIC_CARDS = [
  { key: 'total_views', label: 'Total Views', icon: '👁', color: 'purple' },
  { key: 'total_likes', label: 'Total Likes', icon: '❤️', color: 'pink' },
  { key: 'total_comments', label: 'Comments', icon: '💬', color: 'blue' },
  { key: 'total_shares', label: 'Shares', icon: '🔗', color: 'green' },
  { key: 'total_followers_gained', label: 'New Followers', icon: '👥', color: 'yellow' },
  { key: 'total_revenue_cents', label: 'Revenue', icon: '💰', color: 'emerald', isCurrency: true },
];

export default function CreatorAnalyticsDashboard() {
  const { user, isAuthenticated } = useAuth();
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topVideos, setTopVideos] = useState([]);
  const [audienceInsights, setAudienceInsights] = useState(null);
  const [error, setError] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [summaryRes, dailyRes, videosRes] = await Promise.all([
        apiService.get(`/api/analytics/creator/${user.id}/summary?days=${timeRange}`),
        apiService.get(`/api/analytics/creator/${user.id}?limit=${timeRange}`),
        apiService.get(`/api/analytics/video/creator/${user.id}/top?days=${timeRange}&limit=10`),
      ]);

      setSummary(summaryRes);
      setDailyData(dailyRes || []);
      setTopVideos(videosRes?.videos || []);

      // Try to fetch audience insights (may not exist yet)
      try {
        const audienceRes = await apiService.get(`/api/analytics/creator/${user.id}/audience`);
        setAudienceInsights(audienceRes);
      } catch {
        // Ignore - endpoint may not exist
      }
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAuthenticated, timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Format numbers
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  };

  // Calculate trend percentage
  const calculateTrend = (data, key) => {
    if (!data || data.length < 2) return null;
    const recent = data.slice(0, Math.floor(data.length / 2));
    const older = data.slice(Math.floor(data.length / 2));
    
    const recentSum = recent.reduce((sum, d) => sum + (d[key] || 0), 0);
    const olderSum = older.reduce((sum, d) => sum + (d[key] || 0), 0);
    
    if (olderSum === 0) return recentSum > 0 ? 100 : 0;
    return Math.round(((recentSum - olderSum) / olderSum) * 100);
  };

  // Mini chart component
  const MiniChart = ({ data, dataKey, color }) => {
    const maxValue = Math.max(...data.map(d => d[dataKey] || 0), 1);
    const chartData = data.slice().reverse(); // Oldest to newest
    
    return (
      <div className="flex items-end gap-0.5 h-12">
        {chartData.map((d, i) => (
          <div
            key={i}
            className={`w-1.5 bg-${color}-500 rounded-t opacity-70 hover:opacity-100 transition-opacity`}
            style={{ height: `${((d[dataKey] || 0) / maxValue) * 100}%`, minHeight: '2px' }}
            title={`${d.date}: ${formatNumber(d[dataKey])}`}
          />
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-xl mb-4">Please log in to view analytics</h2>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              📊 Creator Analytics
            </h1>
            
            {/* Time range selector */}
            <div className="flex bg-gray-800 rounded-lg p-1">
              {TIME_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    timeRange === range.value
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {METRIC_CARDS.map((card, index) => {
            const value = summary?.[card.key] ?? 0;
            const trend = calculateTrend(dailyData, card.key.replace('total_', ''));
            
            return (
              <motion.div
                key={card.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-gray-800 rounded-xl p-4 border-l-4 border-${card.color}-500`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{card.icon}</span>
                  <span className="text-gray-400 text-sm">{card.label}</span>
                </div>
                <p className="text-2xl font-bold">
                  {card.isCurrency ? formatCurrency(value) : formatNumber(value)}
                </p>
                {trend !== null && (
                  <p className={`text-xs mt-1 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs prev period
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Views Chart */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              👁 Views Over Time
            </h3>
            {dailyData.length > 0 ? (
              <div className="h-48">
                <div className="flex items-end justify-between h-full gap-1">
                  {dailyData.slice().reverse().map((d, i) => {
                    const maxViews = Math.max(...dailyData.map(x => x.views || 0), 1);
                    const height = ((d.views || 0) / maxViews) * 100;
                    return (
                      <div
                        key={i}
                        className="flex-1 bg-purple-500 rounded-t hover:bg-purple-400 transition-colors cursor-pointer group relative"
                        style={{ height: `${height}%`, minHeight: '4px' }}
                      >
                        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-700 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap">
                          {d.date}: {formatNumber(d.views)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-20">No data available</p>
            )}
          </div>

          {/* Engagement Chart */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              💬 Engagement Breakdown
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl mb-1">❤️</div>
                <p className="text-2xl font-bold">{formatNumber(summary?.total_likes)}</p>
                <p className="text-gray-400 text-sm">Likes</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">💬</div>
                <p className="text-2xl font-bold">{formatNumber(summary?.total_comments)}</p>
                <p className="text-gray-400 text-sm">Comments</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">🔗</div>
                <p className="text-2xl font-bold">{formatNumber(summary?.total_shares)}</p>
                <p className="text-gray-400 text-sm">Shares</p>
              </div>
            </div>
            
            {/* Engagement rate */}
            <div className="mt-6 pt-4 border-t border-gray-700">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Engagement Rate</span>
                <span className="text-xl font-bold text-green-500">
                  {summary?.total_views > 0
                    ? `${(((summary?.total_likes || 0) + (summary?.total_comments || 0)) / summary.total_views * 100).toFixed(2)}%`
                    : '0%'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performing Videos */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            🏆 Top Performing Videos
          </h3>
          
          {topVideos.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No video data available</p>
          ) : (
            <div className="space-y-4">
              {topVideos.map((video, index) => (
                <motion.div
                  key={video.video_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-700 cursor-pointer"
                >
                  {/* Rank */}
                  <div className="w-8 text-center">
                    {index < 3 ? (
                      <span className="text-xl">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </span>
                    ) : (
                      <span className="text-gray-500 font-bold">{index + 1}</span>
                    )}
                  </div>

                  {/* Thumbnail */}
                  <div className="w-20 h-12 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        🎬
                      </div>
                    )}
                  </div>

                  {/* Video info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{video.title || 'Untitled Video'}</p>
                    <p className="text-sm text-gray-400">
                      Posted {new Date(video.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-bold">{formatNumber(video.views)}</p>
                      <p className="text-gray-500">views</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold">{formatNumber(video.likes)}</p>
                      <p className="text-gray-500">likes</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold">{video.avg_watch_percent?.toFixed(0) || 0}%</p>
                      <p className="text-gray-500">avg watch</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Growth & Followers Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Follower Growth */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              👥 Follower Growth
            </h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-3xl font-bold text-green-500">
                  +{formatNumber(summary?.total_followers_gained || 0)}
                </p>
                <p className="text-gray-400 text-sm">New followers</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-red-500">
                  -{formatNumber(Math.abs(summary?.total_followers_lost || 0))}
                </p>
                <p className="text-gray-400 text-sm">Unfollowed</p>
              </div>
            </div>
            <div className="bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-full"
                style={{
                  width: `${Math.min(
                    ((summary?.total_followers_gained || 0) /
                      Math.max((summary?.total_followers_gained || 0) + Math.abs(summary?.total_followers_lost || 0), 1)) *
                      100,
                    100
                  )}%`,
                }}
              />
            </div>
            <p className="text-center text-gray-400 text-sm mt-2">
              Net: +{formatNumber((summary?.total_followers_gained || 0) - Math.abs(summary?.total_followers_lost || 0))}
            </p>
          </div>

          {/* Revenue Summary */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              💰 Revenue Summary
            </h3>
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-emerald-500">
                {formatCurrency(summary?.total_revenue_cents || 0)}
              </p>
              <p className="text-gray-400 mt-2">Total earnings in {timeRange} days</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-700">
              <div className="text-center">
                <p className="text-lg font-bold">{formatCurrency(summary?.avg_daily_revenue || 0)}</p>
                <p className="text-gray-500 text-sm">Avg daily</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold">
                  {formatCurrency((summary?.total_watch_time || 0) > 0 
                    ? (summary?.total_revenue_cents || 0) / (summary?.total_watch_time / 3600) 
                    : 0)}
                </p>
                <p className="text-gray-500 text-sm">Per watch hour</p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Posting Times (if available) */}
        {audienceInsights?.best_posting_times && (
          <div className="bg-gray-800 rounded-xl p-6 mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              ⏰ Best Times to Post
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div key={day} className="text-center">
                  <p className="text-gray-400 text-sm mb-2">{day}</p>
                  <div className="space-y-1">
                    {audienceInsights.best_posting_times[day.toLowerCase()]?.map((hour, i) => (
                      <div
                        key={i}
                        className="bg-purple-500/20 text-purple-400 text-xs py-1 rounded"
                      >
                        {hour}:00
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
